// A full-screen preview of a list's files: live's library preview (its file contained, who made it
// and when, Download, Delete and its details, Previous and Next with the arrow keys, Esc, ✕ and a
// click outside to close), laid out as Daniil's lightbox (spec docs/specs/web/S1-library-screens.md
// decision 21): the file as large as the window allows on a dark, blurred backdrop, its name, kind
// and size on top with the caller's actions, the arrows at the sides (and a swipe), the list's
// pictures in a strip at the foot, and the details behind ⓘ. An image zooms on a click or the
// wheel; a video plays with its sound and its controls; a sound plays in its player. The list may be
// paged: its next page is read within five files of the end, as live's.
import { ActionIcon, Loader, Modal, Text, Tooltip, VisuallyHidden } from "@mantine/core";
import { ChevronLeft, ChevronRight, ExternalLink, Info, Music, X } from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent, type ReactNode, type TouchEvent, type WheelEvent } from "react";

import { AudioPlayer } from "../../ui/AudioPlayer";
import { SoundWave } from "../../ui/SoundWave";
import { VideoFrame } from "../../ui/VideoFrame";
import classes from "./MediaPreview.module.css";

export interface PreviewItem {
  id: string;
  name: string;
  kind: "image" | "video" | "audio";
  /** The file itself, what Download and "Open in new tab" take; null while it has none (in the
   *  trash, or not ready). */
  url: string | null;
  /**
   * A lighter picture of the same image, which the preview shows in the file's place: an image's
   * `urls.preview` (at most 2,560 px, AVIF; #794 PR C), an actor's `screen_url` (issue 910). None,
   * or one that fails to load (a global actor's before the sweep writes it): the file itself. For
   * a video, a lighter picture of its `poster`, shown in the poster's place on the same terms (a
   * global actor's screen-size portrait over its preview video).
   */
  view?: string | null | undefined;
  /** A small picture for the strip: a thumbnail, a poster, or the image itself. */
  thumb: string | null;
  /**
   * A picture of the file shown in its place while the file itself is not served (`url` null): a
   * file in the trash keeps its lighter picture, poster or thumbnail (issue 828, bug 30), shown
   * still, with no player and no zoom past it. None: "This file cannot be shown."
   */
  still?: string | null | undefined;
  poster: string | null;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  /**
   * A search hit's video scene, in seconds (spec S3): the video plays from the frame its card
   * rests on to the scene's end, as live's preview's `#t=` does.
   */
  segment?: { start: number; end: number } | null | undefined;
}

export interface PreviewDetail {
  label: string;
  value: ReactNode;
}

export interface MediaPreviewProps {
  items: readonly PreviewItem[];
  /** The file shown, by id; null while the preview is closed. */
  current: string | null;
  onShow: (id: string | null) => void;
  /** The list's next page, when it has one: read before the end is reached. */
  more?: { has: boolean; loading: boolean; load: () => void } | undefined;
  /** Under the name: who made the file and when (live's header). */
  byline?: ((item: PreviewItem) => ReactNode) | undefined;
  /**
   * The top bar's left in place of the file's name, facts and byline: live's actor preview names
   * no file, only its maker and day (issue 828, bug 25). The dialog keeps the name for a screen
   * reader.
   */
  header?: ((item: PreviewItem) => ReactNode) | undefined;
  /** The top bar's actions on the file shown, before ✕. */
  actions?: ((item: PreviewItem) => ReactNode) | undefined;
  /**
   * What ⓘ shows of the file shown: rows (`DetailList`), or a panel of its own, mounted only
   * while ⓘ is open, so what it reads is read then (the file's generation, G1 decision 30).
   */
  details?: ((item: PreviewItem) => ReactNode) | undefined;
  /** ⓘ open from the start, as a wide pane: live's product preview shows its facts beside the
   *  picture, half the card. */
  detailsOpen?: boolean | undefined;
  /** A dialog over the preview (a delete's question): the arrow keys wait, as live's. */
  paused?: boolean | undefined;
}

/** Where an arrow key is the field's own (live's list): typing, a select, or a player's seek. */
function keysAreTheField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // A sound's seek bar is a slider, whose arrows seek.
  return (
    target.isContentEditable ||
    target.getAttribute("role") === "slider" ||
    ["INPUT", "TEXTAREA", "SELECT", "VIDEO", "AUDIO"].includes(target.tagName)
  );
}

/**
 * Whether a key is another layer's over the preview: an open menu's or list's (the ⋯ menu, which
 * a portal puts outside the preview), or a dialog's opened over it (a Delete's question, later in
 * the page than the preview's own portal). That layer takes its keys, Escape closing it alone; a
 * second Escape, back on the preview, closes the preview (#838: Escape in the ⋯ menu closed the
 * whole preview, as Mantine's modal listens on the window first). A dialog under the preview
 * (Add Actors, which opened it) is not over it: Escape there closes the preview, the topmost.
 */
function keysAreAnotherLayers(target: EventTarget | null, preview: HTMLElement | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('[role="menu"], [role="listbox"]') !== null) return true;
  const dialog = target.closest('[role="dialog"]');
  if (dialog === null || preview === null || dialog === preview) return false;
  return (preview.compareDocumentPosition(dialog) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
}

/** How far a swipe goes before it steps (px), and how far the wheel zooms. */
const SWIPE_PX = 50;
const MAX_ZOOM = 4;
/** How near the list's end the next page is read (live's five). */
const READ_AHEAD = 5;

const KIND_LABEL = { image: "Image", video: "Video", audio: "Audio" } as const;

/** Ours for a file the preview has no picture of (live's shows the browser's broken image). */
const UNSHOWN = "This file cannot be shown.";
/** Live's words for a video that fails to load (`media-video-player`'s
 *  `VIDEO_PREVIEW_UNAVAILABLE_MESSAGE`, #960). */
const VIDEO_UNAVAILABLE = "Video preview unavailable";

/** A length as the app writes one: m:ss, or h:mm:ss, rounded up as a card's badge rounds it
 *  (live's, `scrubTime`), so a 3.44 s clip reads 0:04 in both (issue 828, item 9). */
export function lengthText(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  const [h, m, s] = [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60];
  const two = (value: number) => String(value).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

/** ⓘ's rows: a label and its value each, as the panel lays them out. */
export function DetailList({ details }: { details: readonly PreviewDetail[] }) {
  return (
    <dl className={classes.details}>
      {details.map((detail) => (
        <div key={detail.label} className={classes.detail}>
          <dt>{detail.label}</dt>
          <dd>{detail.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A titled part of ⓘ: a library file's generation, then the file itself. */
export function DetailSection(props: { title: string; action?: ReactNode; children: ReactNode }) {
  const title = <h3 className={classes.sectionTitle}>{props.title}</h3>;
  return (
    <section className={classes.section} aria-label={props.title}>
      {/* A section's own control sits at its title's end (the Image Structure's pencil). */}
      {props.action === undefined ? (
        title
      ) : (
        <div className={classes.sectionHead}>
          {title}
          {props.action}
        </div>
      )}
      {props.children}
    </section>
  );
}

/** The facts on the top bar: the kind, a video's or a sound's length, a picture's size. */
export function factsOf(item: PreviewItem): string[] {
  return [
    KIND_LABEL[item.kind],
    ...(item.kind !== "image" && item.durationMs !== null ? [lengthText(item.durationMs)] : []),
    ...(item.kind !== "audio" && item.width !== null && item.height !== null ? [`${item.width} × ${item.height}`] : []),
  ];
}

export function MediaPreview(props: MediaPreviewProps) {
  const { items, current, onShow, more } = props;
  const index = current === null ? -1 : items.findIndex((item) => item.id === current);
  const item = index < 0 ? undefined : items[index];
  // A file gone from the list (deleted) gives its place to the one after it, or the last.
  const lastIndex = useRef(0);
  useEffect(() => {
    if (index >= 0) {
      lastIndex.current = index;
      return;
    }
    if (current === null) return;
    const next = items[Math.min(lastIndex.current, items.length - 1)];
    onShow(next?.id ?? null);
  }, [index, current, items, onShow]);
  // The next page, read before the end is reached: asked once, though callers pass `more` anew
  // each render.
  const nearEnd = index >= 0 && index >= items.length - READ_AHEAD;
  const loadMore = useRef(more?.load);
  useEffect(() => {
    loadMore.current = more?.load;
  });
  const moreHas = more?.has === true;
  const moreLoading = more?.loading === true;
  useEffect(() => {
    if (nearEnd && moreHas && !moreLoading) loadMore.current?.();
  }, [nearEnd, moreHas, moreLoading]);
  // The pictures either side are fetched and decoded ahead, so a step shows its picture at once
  // (#794; live's preview fetches none ahead). Only once the shown picture has loaded, and at a
  // low priority: on a slow link they would share its bandwidth (model-reviewer's 5.5 s open).
  const [shownLoaded, setShownLoaded] = useState<string | null>(null);
  const settled = item === undefined || item.kind !== "image" || shownOf(item) === null || shownLoaded === shownOf(item);
  const before = index > 0 ? items[index - 1] : undefined;
  const after = index >= 0 ? items[index + 1] : undefined;
  useAhead(settled ? before : undefined);
  useAhead(settled ? after : undefined);
  const hasPrevious = index > 0;
  const hasNext = index >= 0 && index < items.length - 1;
  const step = (by: -1 | 1) => {
    const next = items[index + by];
    if (index >= 0 && next !== undefined) onShow(next.id);
  };
  const [info, setInfo] = useState(props.detailsOpen === true);
  // A preview opened again starts as the first did: a pane open from the start opens again,
  // though it was closed before. Reset while rendering, as the preview closes (React's way to
  // adjust state on a change, without an effect's second render).
  const open = item !== undefined;
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setInfo(props.detailsOpen === true);
  }
  // The arrow keys step through the list, but not from a field or a player, nor under a dialog;
  // Escape closes the preview, but not while a menu or a dialog over it takes its keys.
  const content = useRef<HTMLDivElement>(null);
  const keys = useRef({ step, close: () => onShow(null), active: false });
  useEffect(() => {
    keys.current = { step, close: () => onShow(null), active: item !== undefined && props.paused !== true };
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!keys.current.active || keysAreAnotherLayers(event.target, content.current)) return;
      if (event.key === "Escape") {
        if (!event.isComposing) keys.current.close();
        return;
      }
      if (keysAreTheField(event.target)) return;
      if (event.key === "ArrowLeft") keys.current.step(-1);
      else if (event.key === "ArrowRight") keys.current.step(1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  // A swipe across the file steps through the list (a phone has no arrows to press).
  const touch = useRef<number | null>(null);
  const swiped = (event: TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    const end = event.changedTouches[0]?.clientX;
    if (start === null || end === undefined || Math.abs(end - start) < SWIPE_PX) return;
    step(end < start ? 1 : -1);
  };
  const closeOnDark = (event: MouseEvent) => {
    if (event.target === event.currentTarget) onShow(null);
  };
  return (
    <Modal.Root
      opened={item !== undefined}
      onClose={() => onShow(null)}
      // Escape is the preview's own key (above): Mantine's, on the window before any menu's,
      // closed the preview from inside its ⋯ menu.
      closeOnEscape={false}
      fullScreen
      transitionProps={{ transition: "fade", duration: 150 }}
    >
      <Modal.Overlay className={classes.overlay} />
      <Modal.Content ref={content} className={classes.content} aria-label={item === undefined ? "Preview" : `Preview of ${item.name}`}>
        {item === undefined ? null : (
          <div className={classes.frame}>
            <header className={classes.bar}>
              <div className={classes.title}>
                {props.header === undefined ? (
                  <>
                    <Text className={classes.name} title={item.name}>
                      {item.name}
                    </Text>
                    <Text className={classes.facts}>{factsOf(item).join(" · ")}</Text>
                    {props.byline === undefined ? null : <div className={classes.byline}>{props.byline(item)}</div>}
                  </>
                ) : (
                  props.header(item)
                )}
              </div>
              <div className={classes.actions}>
                {props.actions?.(item)}
                {item.url === null ? null : (
                  <Tooltip label="Open in new tab">
                    <ActionIcon
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="subtle"
                      color="gray"
                      className={classes.action}
                      aria-label={`Open ${item.name} in a new tab`}
                    >
                      <ExternalLink size={18} aria-hidden />
                    </ActionIcon>
                  </Tooltip>
                )}
                {props.details === undefined ? null : (
                  <Tooltip label="Details">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      className={classes.action}
                      aria-label="Details"
                      aria-pressed={info}
                      onClick={() => setInfo(!info)}
                    >
                      <Info size={18} aria-hidden />
                    </ActionIcon>
                  </Tooltip>
                )}
                <Tooltip label="Close">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    className={classes.action}
                    aria-label="Close the preview"
                    onClick={() => onShow(null)}
                  >
                    <X size={20} aria-hidden />
                  </ActionIcon>
                </Tooltip>
              </div>
            </header>
            <div className={classes.middle}>
              {/* The dark around the file closes the preview; the file and the arrows do not. */}
              <div
                className={classes.stage}
                role="presentation"
                onClick={closeOnDark}
                onTouchStart={(event) => {
                  touch.current = event.touches[0]?.clientX ?? null;
                }}
                onTouchEnd={swiped}
              >
                <Media key={item.id} item={item} onDark={() => onShow(null)} onLoaded={setShownLoaded} />
                {/* Live's arrows: none for a single file, off at the ends (no wrap). */}
                {items.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className={classes.side}
                      data-side="previous"
                      aria-label="Previous"
                      disabled={!hasPrevious}
                      onClick={() => step(-1)}
                    >
                      <ChevronLeft size={28} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={classes.side}
                      data-side="next"
                      aria-label="Next"
                      disabled={!hasNext}
                      onClick={() => step(1)}
                    >
                      <ChevronRight size={28} aria-hidden />
                    </button>
                  </>
                ) : null}
              </div>
              {info && props.details !== undefined ? (
                <aside className={classes.info} data-pane={props.detailsOpen === true || undefined} aria-label="Details">
                  {props.details(item)}
                </aside>
              ) : null}
            </div>
            <Strip items={items} current={index} onShow={onShow} loading={more?.loading === true} />
          </div>
        )}
      </Modal.Content>
    </Modal.Root>
  );
}

/** What the preview shows of an image: its lighter picture where it has one, else the file. */
function shownOf(item: PreviewItem): string | null {
  return item.view ?? item.url;
}

/** An image slide's picture, fetched and decoded before it shows; nothing for any other kind. */
function useAhead(item: PreviewItem | undefined) {
  const url = item?.kind === "image" ? shownOf(item) : null;
  useEffect(() => {
    if (url === null) return;
    const picture = new Image();
    picture.decoding = "async";
    picture.fetchPriority = "low";
    picture.src = url;
    void picture.decode?.().catch(() => undefined);
  }, [url]);
}

/**
 * The file, as large as the window allows with its shape kept: a picture that zooms (a click
 * toggles twice its size at the point clicked; the wheel zooms up to four times), a video playing
 * with its sound and its controls, or a sound's player.
 */
function Media(props: { item: PreviewItem; onDark: () => void; onLoaded: (url: string) => void }) {
  const { item, onDark, onLoaded } = props;
  const [zoom, setZoom] = useState({ scale: 1, x: 50, y: 50 });
  // The picture's own file, once it has loaded: until then its thumbnail stands in.
  const [loaded, setLoaded] = useState<string | null>(null);
  const picture = useRef<HTMLImageElement>(null);
  // The file itself, decoded, over its lighter preview once a zoom asks for more pixels than the
  // preview has: live's preview is the file, so its deep zoom stays sharp (issue 794).
  const [sharp, setSharp] = useState<string | null>(null);
  const asked = useRef(false);
  // The lighter picture that failed to load: the file stands in for it.
  const [failed, setFailed] = useState<string | null>(null);
  // The file itself that failed to load (one the bucket cannot give, or cannot be decoded):
  // nothing stands in, so the preview says so where the spinner would turn forever. The failed
  // picture or video stays mounted, unseen: the media cookies' retry asks again for that very
  // element once a renewal lands (media-cookies Design 4), and its load shows it.
  const [broken, setBroken] = useState<string | null>(null);
  if (item.url === null) {
    const still = item.still ?? null;
    if (still !== null) {
      return (
        <img src={still} alt={item.name} decoding="async" className={classes.image} onLoad={() => onLoaded(still)} />
      );
    }
    return (
      <div className={classes.missing} role="presentation" onClick={onDark}>
        <Text>{UNSHOWN}</Text>
      </div>
    );
  }
  if (item.kind === "video") {
    // The lighter poster once a hidden copy of it has loaded, else the poster itself: a video says
    // nothing of a poster that fails to load, so the copy finds out first.
    const lighter = item.view ?? null;
    const settled = lighter === null || loaded === lighter || failed === lighter;
    const poster = lighter === null || failed === lighter ? item.poster : loaded === lighter ? lighter : null;
    const src = item.segment == null ? item.url : `${item.url}#t=${item.segment.start},${item.segment.end}`;
    // Live's words over a video that fails to load, until its data loads (#960).
    const unavailable = broken === src;
    return (
      <>
        {settled ? null : (
          <img src={lighter} alt="" hidden onLoad={() => setLoaded(lighter)} onError={() => setFailed(lighter)} />
        )}
        {/* A preview has no captions to give, and plays the file's own sound, as live's (spec
            web/player-sound). */}
        {/* oxlint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          className={classes.video}
          src={src}
          poster={poster ?? undefined}
          controls
          autoPlay
          playsInline
          aria-label={item.name}
          data-broken={unavailable || undefined}
          aria-hidden={unavailable || undefined}
          onError={() => setBroken(src)}
          onLoadedData={() => setBroken(null)}
        />
        {unavailable ? (
          <Text component="span" className={classes.unavailable}>
            {VIDEO_UNAVAILABLE}
          </Text>
        ) : null}
      </>
    );
  }
  if (item.kind === "audio") return <Sound item={item} url={item.url} />;
  const preferred = shownOf(item) ?? item.url;
  const url = failed === preferred ? item.url : preferred;
  const point = (event: MouseEvent<HTMLButtonElement> | WheelEvent<HTMLButtonElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const x = box.width > 0 ? ((event.clientX - box.left) / box.width) * 100 : 50;
    const y = box.height > 0 ? ((event.clientY - box.top) / box.height) * 100 : 50;
    return { x, y };
  };
  const file = item.url;
  // Past the preview's own pixels (its width against the width the zoom draws it at on this
  // screen), the file is fetched once and decoded before it shows; the preview stays meanwhile.
  const sharpen = (scale: number) => {
    const shown = picture.current;
    if (item.view == null || item.view === file || asked.current || shown === null || loaded !== url) return;
    if (item.width !== null && item.width <= shown.naturalWidth) return;
    if (shown.clientWidth * scale * window.devicePixelRatio <= shown.naturalWidth) return;
    asked.current = true;
    const original = new Image();
    original.decoding = "async";
    original.src = file;
    void original.decode?.().then(
      () => setSharp(file),
      () => {
        asked.current = false;
      },
    );
  };
  const zoomed = zoom.scale > 1;
  const transform = { transform: `scale(${zoom.scale})`, transformOrigin: `${zoom.x}% ${zoom.y}%` };
  const unshown = broken === url;
  return (
    <button
      type="button"
      className={classes.zoom}
      disabled={unshown}
      data-zoomed={zoomed || undefined}
      aria-label={unshown ? UNSHOWN : zoomed ? "Zoom out" : "Zoom in"}
      onClick={(event) => {
        setZoom(zoomed ? { scale: 1, x: 50, y: 50 } : { scale: 2, ...point(event) });
        if (!zoomed) sharpen(2);
      }}
      onWheel={(event) => {
        const scale = Math.min(MAX_ZOOM, Math.max(1, zoom.scale * (event.deltaY < 0 ? 1.2 : 1 / 1.2)));
        setZoom({ scale, ...(scale > 1 ? point(event) : { x: 50, y: 50 }) });
        sharpen(scale);
      }}
    >
      <span className={classes.layers}>
        {/* While the file loads, the grid's thumbnail (in the cache already) stands in at the
            file's size (its width and height: the box is the file's, the picture contained in it),
            under live's spinner (design-ref/speed-794 01, 03; live shows the spinner on the dark
            alone): no blank frame, no picture painting top-down (issue 794). */}
        {loaded === url || unshown || item.thumb === null || item.thumb === url ? null : (
          <img
            src={item.thumb}
            alt=""
            className={classes.image}
            {...(item.width === null || item.height === null ? {} : { width: item.width, height: item.height })}
          />
        )}
        <img
          ref={picture}
          src={url}
          alt={item.name}
          decoding="async"
          fetchPriority="high"
          className={classes.image}
          data-loading={loaded === url ? undefined : true}
          data-broken={unshown || undefined}
          aria-hidden={unshown || undefined}
          style={transform}
          onLoad={() => {
            setLoaded(url);
            setBroken(null);
            onLoaded(preferred);
          }}
          onError={() => {
            if (url !== item.url) setFailed(url);
            else setBroken(url);
          }}
        />
        {sharp === file ? <img src={file} alt="" className={classes.image} style={transform} /> : null}
        {unshown ? (
          <Text component="span" className={classes.missing}>
            {UNSHOWN}
          </Text>
        ) : null}
        {loaded === url || unshown ? null : <Loader className={classes.loading} aria-label="Loading the file" />}
      </span>
    </button>
  );
}

/** A strip tile's picture, and the kind's glyph over it while it fails to load; the picture
 *  stays mounted for its retry (media-cookies Design 4), and its load takes the glyph away. */
function StripPicture({ src, kind }: { src: string; kind: PreviewItem["kind"] }) {
  const [failed, setFailed] = useState<string | null>(null);
  const covered = failed === src;
  return (
    <>
      <img
        src={src}
        alt=""
        className={classes.stripPicture}
        data-failed={covered || undefined}
        loading="lazy"
        decoding="async"
        onLoad={() => setFailed(null)}
        onError={() => setFailed(src)}
      />
      {covered ? <StripGlyph kind={kind} /> : null}
    </>
  );
}

/** A strip tile with no picture: a sound's note, or the kind's name. */
function StripGlyph({ kind }: { kind: PreviewItem["kind"] }) {
  return (
    <span className={classes.stripGlyph} aria-hidden>
      {kind === "audio" ? <Music size={18} /> : KIND_LABEL[kind]}
    </span>
  );
}

/**
 * A sound: live's bars (`SoundWave`, the sound tiles' picture; nothing here draws a sound's real
 * waveform), moving while it plays, over live's player (`AudioPlayer`, the Sounds page's), which
 * starts at once and stops any other player on the page.
 */
function Sound({ item, url }: { item: PreviewItem; url: string }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className={classes.sound}>
      <SoundWave className={classes.soundWave} animated={playing} />
      <div className={classes.soundPlayer}>
        <AudioPlayer
          url={url}
          name={item.name}
          autoPlay
          durationSeconds={item.durationMs === null ? null : item.durationMs / 1000}
          onPlayingChange={setPlaying}
        />
      </div>
    </div>
  );
}

/** The list's pictures in a strip, the one shown marked and kept in view; a click shows another. */
function Strip(props: { items: readonly PreviewItem[]; current: number; onShow: (id: string) => void; loading: boolean }) {
  const shown = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    shown.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [props.current]);
  return (
    <nav className={classes.strip} aria-label="Files">
      <ol className={classes.stripList}>
        {props.items.map((item, at) => (
          <li key={item.id}>
            <button
              type="button"
              ref={at === props.current ? shown : undefined}
              className={classes.stripItem}
              aria-current={at === props.current ? "true" : undefined}
              aria-label={item.name}
              onClick={() => props.onShow(item.id)}
            >
              {item.thumb !== null ? (
                <StripPicture src={item.thumb} kind={item.kind} />
              ) : item.kind === "video" && item.url !== null ? (
                // No poster yet, as the library's card: a still frame of the video itself.
                <VideoFrame src={item.url} still fallback={<StripGlyph kind={item.kind} />} />
              ) : (
                <StripGlyph kind={item.kind} />
              )}
            </button>
          </li>
        ))}
      </ol>
      {props.loading ? <VisuallyHidden>Loading more files</VisuallyHidden> : null}
    </nav>
  );
}
